// apps/web/src/api/refresh.ts
// handling. Any 5xx is a NetworkError (the refresh cookie may still be valid);
// only 4xx is an authoritative session-loss signal. The coordinator itself
// never clears the store — that is the api-client's job (see api/client.ts).
import { WebRefreshResponseSchema } from '@magimp/shared';
import { useAuthStore } from '../stores/auth.js';
import { NetworkError, ThrottleError, SessionChangedError, parseApiError } from './errors.js';
import { withRefreshLock } from './refreshLock.js';

let inFlight: Promise<string> | null = null; // resolves to the NEW accessToken

// Cooldown tracking: when the backend returns 429 we record the deadline
// (epoch ms) until which any new coordinatedRefresh() call short-circuits
// to a ThrottleError without touching the network. Without this, staggered
// 401s arriving across queries keep firing fresh /auth/refresh requests
// inside a saturated throttle window — exactly the back-pressure
// amplification @Throttle is designed to dampen.
let throttledUntil = 0;
const DEFAULT_COOLDOWN_MS = 5_000;

// INVARIANT: bounded refresh fetch timeout. Without this, a hung fetch
// (e.g. ERR_NETWORK_CHANGED stall, slow proxy) would hold the cross-tab
// Web Lock indefinitely, stranding every other tab in the same browser
// profile in `Restoring session…`. 10 s matches the fallback lease in
// `refreshLock.ts`. On timeout the AbortController fires and `fetch`
// rejects; we rewrap as `NetworkError` to preserve the session.
const REFRESH_FETCH_TIMEOUT_MS = 10_000;

function parseRetryAfter(headers: Headers): number | null {
  const v = headers.get('Retry-After');
  if (!v) return null;
  const seconds = Number(v);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
  // HTTP-date format fallback (Retry-After can also be an HTTP-date).
  const date = Date.parse(v);
  if (Number.isFinite(date)) {
    const delta = date - Date.now();
    return delta > 0 ? delta : null;
  }
  return null;
}

function makeTimeoutSignal(ms: number): AbortSignal {
  // Prefer the native helper. Manual fallback covers older runtimes that
  // strip `AbortSignal.timeout`; the contract stays identical.
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms);
  }
  const ac = new AbortController();
  setTimeout(() => ac.abort(new DOMException('Refresh timeout', 'TimeoutError')), ms);
  return ac.signal;
}

/**
 * @internal Test-only: clear the coordinator's mutable state so each test
 *   starts from a known baseline. Production code never calls this. The MODE
 *   guard fails loudly if it ever does — a stray prod import would otherwise
 *   silently erase an active inFlight refresh or cooldown, reopening the
 *   duplicate-refresh path this module exists to close.
 */
export function __resetCoordinatorState(): void {
  if (import.meta.env.MODE !== 'test') {
    throw new Error('__resetCoordinatorState is test-only');
  }
  inFlight = null;
  throttledUntil = 0;
}

/**
 * @aiPurpose single-flight token refresh with universal session-revision
 *   guard plus cross-tab serialization. Per-tab callers share one
 *   in-flight promise; across tabs the refresh critical section runs
 *   under `magimp:auth-refresh:v1` (Web Locks API or a localStorage
 *   lease fallback) so two tabs in the same browser profile cannot
 *   submit parallel `POST /api/auth/refresh` and trip backend reuse
 *   detection.
 * @aiInvariant `sessionRevision` is captured BEFORE the cross-tab lock
 *   wait and re-checked at three points: just before the fetch fires
 *   (catches a sister tab's session-lost broadcast that landed while we
 *   queued for the lock), on 4xx error classification, and before
 *   `setAccessToken()`. If we captured the revision INSIDE the lock the
 *   pre-fetch check would compare new-against-new and miss broadcasts
 *   that arrived during the queue wait — auth-security regression.
 * @aiSecurity 4xx errors from the backend are authoritative auth-loss
 *   signals ONLY when the session revision is unchanged. If the session
 *   changed during flight, the 4xx is reclassified as
 *   `SessionChangedError` (a `NetworkError` subclass), preserving the
 *   current session.
 */
export async function coordinatedRefresh(): Promise<string> {
  if (Date.now() < throttledUntil) {
    throw new ThrottleError('Refresh throttled (cooldown active)');
  }
  if (inFlight) return inFlight;
  // INVARIANT: capture BEFORE `await withRefreshLock`. While we queue
  // for the cross-tab lock, a sister tab's session-lost broadcast may
  // arrive and bump our local sessionRevision via the receive handler.
  // If we captured inside the lock, the post-wait capture would already
  // be the new revision and no guard check would catch the mismatch.
  const capturedRevision = useAuthStore.getState().sessionRevision;
  inFlight = (async () => {
    try {
      return await withRefreshLock(async () => {
        // Pre-fetch guard: a session-lost broadcast (or a local
        // logout/clear) that landed while we waited for the lock has
        // already bumped our revision. Reject without firing the
        // network refresh — the cookie may have just been rotated by
        // the holder before us, but our session already ended.
        if (useAuthStore.getState().sessionRevision !== capturedRevision) {
          throw new SessionChangedError('Session changed while waiting for refresh lock');
        }
        let res: Response;
        try {
          res = await fetch('/api/auth/refresh', {
            method: 'POST',
            credentials: 'include',
            signal: makeTimeoutSignal(REFRESH_FETCH_TIMEOUT_MS),
          });
        } catch (e) {
          // Includes timeout/abort, ERR_NETWORK_CHANGED, offline, etc.
          throw new NetworkError('Network unavailable during refresh', { cause: e });
        }
        if (res.status === 429) {
          const cooldownMs = parseRetryAfter(res.headers) ?? DEFAULT_COOLDOWN_MS;
          throttledUntil = Date.now() + cooldownMs;
          throw new ThrottleError('Refresh throttled: HTTP 429');
        }
        if (res.status >= 500) {
          throw new NetworkError(`Refresh temporarily unavailable: HTTP ${res.status}`);
        }
        if (!res.ok) {
          const err = await parseApiError(res);
          if (useAuthStore.getState().sessionRevision !== capturedRevision) {
            throw new SessionChangedError('Session changed; auth error from old session discarded');
          }
          throw err;
        }
        let raw: unknown;
        try {
          raw = await res.json();
        } catch (e) {
          throw new NetworkError('Refresh response not valid JSON', { cause: e });
        }
        const parsed = WebRefreshResponseSchema.safeParse(raw);
        if (!parsed.success) {
          throw new NetworkError('Refresh response shape invalid', { cause: parsed.error });
        }
        if (useAuthStore.getState().sessionRevision !== capturedRevision) {
          throw new SessionChangedError('Session changed during refresh');
        }
        useAuthStore.getState().setAccessToken(parsed.data.accessToken);
        return parsed.data.accessToken;
      });
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}
